import React from "react";
import { ParseText, Reveal, StaggerGroup, RevealItem } from "@common";
import { fadeLeft } from "@common/motion/variants";
import {
  SectionWrapper,
  SectionContent,
  TopRow,
  TopLeftSection,
  TopRightSection,
  Title,
  Subtitle,
  ContactList,
  ContactItem,
  LinksColumn,
  LinksTitle,
  LinksList,
  FooterLink,
  FooterExternalLink,
  BottomSection,
  CopyrightWrapper,
  CopyrightText,
  Slogan,
  MassiveText,
  SocialRow,
  SocialSectionLabel,
  SocialChipsContainer,
  SocialChip,
  SocialChipIcon,
} from "./FinalSection.styles";

// SVG Icons
const MapPinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
  </svg>
);

const YoutubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.42a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.42 8.6.42 8.6.42s6.88 0 8.6-.42a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.33 29 29 0 0 0-.46-5.33z"></path>
    <polygon points="9.75,15.02 15.5,11.75 9.75,8.48"></polygon>
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
    <rect x="2" y="9" width="4" height="12"></rect>
    <circle cx="4" cy="4" r="2"></circle>
  </svg>
);

function FinalSection({ content }) {
  if (!content) return null;
  return (
    <SectionWrapper>
      <SectionContent>
        <TopRow>
          <StaggerGroup as={TopLeftSection}>
            <RevealItem as={Title}>
              <ParseText text={content.title} />
            </RevealItem>
            <RevealItem as={Subtitle}>{content.subtitle}</RevealItem>
          </StaggerGroup>

          <StaggerGroup as={TopRightSection}>

            <StaggerGroup as={ContactList} stagger={0.08}>
              <RevealItem as={ContactItem} variants={fadeLeft}>
                <MapPinIcon />
                <span>{content.address}</span>
              </RevealItem>
              <RevealItem
                as={ContactItem}
                variants={fadeLeft}
                onClick={() => { window.location.href = `tel:${content.phone}`; }}
                style={{ cursor: 'pointer' }}
              >
                <PhoneIcon />
                <span>{content.phone}</span>
              </RevealItem>
              <RevealItem
                as={ContactItem}
                variants={fadeLeft}
                onClick={() => { window.location.href = `mailto:${content.email}`; }}
                style={{ cursor: 'pointer' }}
              >
                <MailIcon />
                <span>{content.email}</span>
              </RevealItem>
            </StaggerGroup>

            <SocialRow>
              <SocialSectionLabel>Find us on</SocialSectionLabel>
              <SocialChipsContainer>
                <SocialChip
                  $platform="facebook"
                  onClick={() => window.open("https://www.facebook.com/people/Tatva-Wellness/61555415983817/", "_blank")}
                >
                  <SocialChipIcon><FacebookIcon /></SocialChipIcon>
                </SocialChip>
                <SocialChip
                  $platform="instagram"
                  onClick={() => window.open("https://www.instagram.com/tatva_wellness?igsh=aHhkcGdhMnp3dGIy", "_blank")}
                >
                  <SocialChipIcon><InstagramIcon /></SocialChipIcon>
                </SocialChip>
                <SocialChip
                  $platform="youtube"
                  onClick={() => window.open("https://www.youtube.com/@TatvaWellness", "_blank")}
                >
                  <SocialChipIcon><YoutubeIcon /></SocialChipIcon>
                </SocialChip>
                <SocialChip
                  $platform="linkedin"
                  onClick={() => window.open("https://www.linkedin.com/company/tatva-app/", "_blank")}
                >
                  <SocialChipIcon><LinkedInIcon /></SocialChipIcon>
                </SocialChip>
              </SocialChipsContainer>
            </SocialRow>
          </StaggerGroup>
        </TopRow>

        <Reveal as={BottomSection}>
          <CopyrightWrapper>
            <CopyrightText>{content.copyright}</CopyrightText>
            <Slogan>{content.slogan}</Slogan>
          </CopyrightWrapper>
        </Reveal>
      </SectionContent>
    </SectionWrapper>
  );
}

export default FinalSection;
